import { Dataset, DataFlowDSLAdapterRegistry, LinkedService, Metamodel, Owned } from 'shell-models-lib';
import {
    DataFlowDatasetSettings,
    DatasetFormatType
} from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DataFlowExpressionUtils } from '../expression.util';
import {
    IntegerDataFlowType,
    BooleanDataFlowType,
    StringDataFlowType,
    TimeStampDataFlowType,
    ArrayDataFlowType
} from 'app/model/dataflow/dataflow-datatype';
import { BaseFormatAdapter } from './base-format.adapter';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import {
    DeltaSettings,
    DeltaTableActionOptions,
    IPartitionPruningCondition,
    TimeTravelOption
} from 'app/model/dataflow/dataset-settings/delta-dataset-settings';
import { FILEMODE } from 'app/model/dataflow/dataset-settings/file-based-settings';
import { DataFlowSchemaAdapterUtils } from '../dataflow-schema-adapter-utils';
import { LinkedServiceUtils } from 'app/model/linkedservice/linked-service.utils';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { getAttributeValueFromDataset } from 'app/model/dataset/dataset.utils';
import { DataFlowAdapterUtils } from '../adapter.util';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(DeltaSettings)
})
export class DeltaFormatAdapter extends BaseFormatAdapter<DeltaSettings> {
    public supplementPropsForFabricMigration(
        dataset: Dataset | undefined,
        _linkedService: LinkedService | undefined,
        props: string[],
        _isSource: boolean,
        isInline: boolean,
        _datasetSettings: DataFlowDatasetSettings | undefined
    ): void {
        if (!isInline && dataset) {
            props.push(`format: '${DatasetFormatType.Delta}'`);

            const schema = getAttributeValueFromDataset(dataset, 'schema');
            const table = getAttributeValueFromDataset(dataset, 'table');
            if (table) {
                const tableName = schema ? `${schema}/${table}` : table;
                props.push(`tableName: ${DataFlowAdapterUtils.formatExpressionValue(tableName)}`);
            }
        }
    }

    protected async toSourceDSL(settings: DeltaSettings): Promise<string[]> {
        const props = [];
        const model = settings.owner as DataFlowSource;
        props.push(`format: '${DatasetFormatType.Delta}'`);
        if (settings.compressionType) {
            props.push(`compressionType: '${settings.compressionType}'`);
            props.push(
                `compressionLevel: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.compressionLevel)}`
            );
        }
        if (settings.timeTravelOption === TimeTravelOption.Timestamp && settings.timestampAsOf.expression) {
            props.push(
                `timestampAsOf: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    settings.timestampAsOf,
                    TimeStampDataFlowType.TYPE
                )}`
            );
        }
        if (settings.timeTravelOption === TimeTravelOption.Version && settings.versionAsOf.expression) {
            props.push(
                `versionAsOf: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    settings.versionAsOf,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        if (settings.container.expression) {
            if (LinkedServiceUtils.isBucketNameTypeInBackend(model.sourceLinkedService)) {
                props.push(`bucketName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.container)}`);
            } else if (LinkedServiceUtils.isFileSystemTypeInBackend(model.sourceLinkedService)) {
                props.push(`fileSystem: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.container)}`);
            } else {
                props.push(`container: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.container)}`);
            }
        }
        if (settings.folderPath.expression) {
            props.push(`folderPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.folderPath)}`);
        } else if (settings.tableName.expression) {
            props.push(`tableName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.tableName)}`);
        }
        return props;
    }

    protected async toSinkDSL(settings: DeltaSettings): Promise<string[]> {
        const props = [];
        const model = settings.owner as DataFlowSink;
        props.push(`format: '${DatasetFormatType.Delta}'`);
        if (settings.compressionType) {
            props.push(`compressionType: '${settings.compressionType}'`);
            props.push(
                `compressionLevel: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.compressionLevel)}`
            );
        }
        if (settings.container.expression) {
            if (LinkedServiceUtils.isBucketNameTypeInBackend(model.sinkLinkedService)) {
                props.push(`bucketName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.container)}`);
            } else if (LinkedServiceUtils.isFileSystemTypeInBackend(model.sinkLinkedService)) {
                props.push(`fileSystem: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.container)}`);
            } else {
                props.push(`container: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.container)}`);
            }
        }
        if (settings.folderPath.expression) {
            props.push(`folderPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.folderPath)}`);
        } else if (settings.tableName.expression) {
            props.push(`tableName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.tableName)}`);
        }
        if (settings.tableActionOption === DeltaTableActionOptions.Truncate) {
            props.push(`truncate: true`);
        }
        if (settings.tableActionOption === DeltaTableActionOptions.DeltaTruncate) {
            props.push(`deltaTruncate: true`);
        }
        if (settings.tableActionOption === DeltaTableActionOptions.Overwrite) {
            props.push(`overwrite: true`);
        }
        if (settings.mergeSchema.expression) {
            props.push(
                `mergeSchema: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    settings.mergeSchema,
                    BooleanDataFlowType.TYPE
                )}`
            );
        }
        if (settings.autoCompact.expression) {
            props.push(
                `autoCompact: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    settings.autoCompact,
                    BooleanDataFlowType.TYPE
                )}`
            );
        }
        if (settings.optimizedWrite.expression) {
            props.push(
                `optimizedWrite: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    settings.optimizedWrite,
                    BooleanDataFlowType.TYPE
                )}`
            );
        }
        if (settings.vacuum.expression) {
            props.push(
                `vacuum: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    settings.vacuum,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        props.push(
            `deletable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                settings.allowDelete,
                BooleanDataFlowType.TYPE
            )}`
        );
        props.push(
            `insertable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                settings.allowInsert,
                BooleanDataFlowType.TYPE
            )}`
        );
        props.push(
            `updateable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                settings.allowUpdate,
                BooleanDataFlowType.TYPE
            )}`
        );
        props.push(
            `upsertable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                settings.allowUpsert,
                BooleanDataFlowType.TYPE
            )}`
        );
        const keyColumns = DataFlowSchemaAdapterUtils.getKeyColumnsProps(settings);
        if (keyColumns) {
            props.push(keyColumns);
        }
        if (settings.pruneConditions) {
            const pruneConditions = settings.pruneConditions.filter(
                (cond) => cond.colName.expression && cond.values.expression
            );
            const conditions = pruneConditions
                .map(
                    (cond) =>
                        `${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            cond.colName
                        )} -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(cond.values, ArrayDataFlowType.TYPE)}`
                )
                .join(',');
            if (conditions) {
                props.push(`pruneCondition: [${conditions}]`);
            }
        }

        return props;
    }

    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<DeltaSettings> {
        const settings = Owned.create<DeltaSettings>(DeltaSettings.MODEL, owner);
        settings.initialize(undefined);
        settings.fileMode = FILEMODE.FILE;

        if (configs.has('container')) {
            settings.container = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('container'), owner);
            handledConfigs.push('container');
        } else if (configs.has('fileSystem')) {
            settings.container = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('fileSystem'), owner);
            handledConfigs.push('fileSystem');
        } else if (configs.has('bucketName')) {
            settings.container = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('bucketName'), owner);
            handledConfigs.push('bucketName');
        }
        if (configs.has('folderPath')) {
            settings.folderPath = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('folderPath'), owner);
            handledConfigs.push('folderPath');
        } else if (configs.has('tableName')) {
            settings.tableName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('tableName'), owner);
            handledConfigs.push('tableName');
        }
        if (configs.has('compressionType')) {
            settings.compressionType = DataFlowExpressionUtils.extractStringConfigValue(configs.get('compressionType'));
            handledConfigs.push('compressionType');
        }
        if (configs.has('compressionLevel')) {
            settings.compressionLevel = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('compressionLevel'),
                owner
            );
            handledConfigs.push('compressionLevel');
        }
        if (configs.has('timestampAsOf')) {
            settings.timestampAsOf = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('timestampAsOf'),
                owner,
                TimeStampDataFlowType.TYPE
            );
            settings.timeTravelOption = TimeTravelOption.Timestamp;
            handledConfigs.push('timestampAsOf');
        }
        if (configs.has('versionAsOf')) {
            settings.versionAsOf = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('versionAsOf'),
                owner,
                IntegerDataFlowType.TYPE
            );
            settings.timeTravelOption = TimeTravelOption.Version;
            handledConfigs.push('versionAsOf');
        }

        return settings;
    }

    protected async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<DeltaSettings> {
        const deltaSettings = Owned.create<DeltaSettings>(DeltaSettings.MODEL, owner);
        deltaSettings.initialize(undefined);
        deltaSettings.fileMode = FILEMODE.FILE;

        if (configs.has('container')) {
            deltaSettings.container = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('container'),
                owner
            );
            handledConfigs.push('container');
        } else if (configs.has('fileSystem')) {
            deltaSettings.container = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('fileSystem'),
                owner
            );
            handledConfigs.push('fileSystem');
        } else if (configs.has('bucketName')) {
            deltaSettings.container = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('bucketName'),
                owner
            );
            handledConfigs.push('bucketName');
        }
        if (configs.has('folderPath')) {
            deltaSettings.folderPath = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('folderPath'),
                owner
            );
            handledConfigs.push('folderPath');
        } else if (configs.has('tableName')) {
            deltaSettings.tableName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('tableName'),
                owner
            );
            handledConfigs.push('tableName');
        }
        if (configs.has('compressionType')) {
            deltaSettings.compressionType = DataFlowExpressionUtils.extractStringConfigValue(
                configs.get('compressionType')
            );
            handledConfigs.push('compressionType');
        }
        if (configs.has('compressionLevel')) {
            deltaSettings.compressionLevel = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('compressionLevel'),
                owner
            );
            handledConfigs.push('compressionLevel');
        }
        if (configs.has('insertable')) {
            deltaSettings.allowInsert = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('insertable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('insertable');
        }
        if (configs.has('updateable')) {
            deltaSettings.allowUpdate = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('updateable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('updateable');
        }
        if (configs.has('upsertable')) {
            deltaSettings.allowUpsert = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('upsertable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('upsertable');
        }
        if (configs.has('deletable')) {
            deltaSettings.allowDelete = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('deletable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('deletable');
        }
        if (configs.has('keys')) {
            const expression = configs.get('keys');
            deltaSettings.isListOfKeyColumns = expression.startsWith('[') && expression.endsWith(']');
            deltaSettings.keyColumns = deltaSettings.isListOfKeyColumns
                ? DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, owner, StringDataFlowType.TYPE, true)
                : [DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, owner, 'string[]', true)];
            handledConfigs.push('keys');
        }
        if (configs.has('pruneCondition')) {
            const conds: IPartitionPruningCondition[] = DataFlowExpressionUtils.parseExpressionMapInDsl(
                configs.get('pruneCondition'),
                owner
            ).map(({ paramName, paramValue }) => {
                return { colName: paramName as DataFlowExpression, values: paramValue as DataFlowExpression };
            });
            if (conds.length > 0) {
                deltaSettings.pruneConditions = conds;
            }
            handledConfigs.push('pruneCondition');
        }
        if (configs.has('truncate')) {
            deltaSettings.tableActionOption =
                DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    configs.get('truncate'),
                    owner,
                    BooleanDataFlowType.TYPE
                ).expression === 'true'
                    ? DeltaTableActionOptions.Truncate
                    : deltaSettings.tableActionOption;
            handledConfigs.push('truncate');
        }
        if (configs.has('deltaTruncate')) {
            deltaSettings.tableActionOption =
                DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    configs.get('deltaTruncate'),
                    owner,
                    BooleanDataFlowType.TYPE
                ).expression === 'true'
                    ? DeltaTableActionOptions.DeltaTruncate
                    : deltaSettings.tableActionOption;
            handledConfigs.push('deltaTruncate');
        }
        if (configs.has('overwrite')) {
            deltaSettings.tableActionOption =
                DataFlowExpressionUtils.unwrapExpressionConfigValue(
                    configs.get('overwrite'),
                    owner,
                    BooleanDataFlowType.TYPE
                ).expression === 'true'
                    ? DeltaTableActionOptions.Overwrite
                    : deltaSettings.tableActionOption;
            handledConfigs.push('overwrite');
        }
        if (configs.has('mergeSchema')) {
            deltaSettings.mergeSchema = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('mergeSchema'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('mergeSchema');
        }
        if (configs.has('autoCompact')) {
            deltaSettings.autoCompact = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('autoCompact'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('autoCompact');
        }
        if (configs.has('optimizedWrite')) {
            deltaSettings.optimizedWrite = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('optimizedWrite'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('optimizedWrite');
        }
        if (configs.has('vacuum')) {
            deltaSettings.vacuum = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('vacuum'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('vacuum');
        }

        return deltaSettings;
    }
}
